import styled from "styled-components";
import BreadCrumbs from "../../../../components/ui-v2/BreadCrumbs";
import { ROUTES } from "../../../../router";
import LabeledTextInput from "../../../../components/ui-v2/Inputs/LabeledTextInput";
import Dropdown from "../../../../components/ui-v2/Dropdown";
import ToggleSwitch from "../../../../components/ui-v2/Inputs/ToggleSwitch";
import Button from "../../../../components/ui-v2/Button";
import { UilInfoCircle } from "@iconscout/react-unicons";
import Modal from "../../../../components/ui-v2/Modal";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  TCellStyleVariants,
  TTableBody,
  TTableSelected,
} from "../../../../components/ui-v2/TableGrid";
import {
  TOrganization,
  TOrganizationData,
} from "../../../../redux/reducers/organizations";
import {
  backendErrorMessageHandler,
  decideMembershipRole,
  getQuerystringParams,
  validateEmail,
} from "../../../../utils/miscellaneous";
import apiService from "../../../../services/api";
import {
  TMembership,
  TMembershipRole,
} from "../../../../redux/reducers/memberships";
import { useNavigationManager } from "../../../../hooks/useNavigationManager";
import axios, { AxiosError, CancelTokenSource } from "axios";
import { setToastState } from "../../../../redux/reducers/globalState";
import { useTypedDispatch } from "../../../../hooks/useTypedDispatch";
import Tooltip from "../../../../components/ui-v2/Tooltip";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import { cnpjMask } from "../../../../utils/formatters";
import {
  setOrganizationsByMembershipThunk,
  createMemberThunk,
  editMemberThunk,
  fetchOrganizationsForUserSettings,
} from "../../../../redux/actions";
import { useParams } from "react-router-dom";

const Container = styled.main`
  position: relative;

  & > *:nth-child(1) {
    margin-top: 20px;
  }

  h1 {
    font-family: var(--font-title-bold);
    font-size: 28px;
    color: var(--color-text-title);
    margin-top: 10px;
    margin-bottom: 20px;
  }

  h2:not(h2:first-of-type) {
    font-family: var(--font-title-bold);
    font-size: 21px;
    color: var(--color-text-paragraph);
    margin-top: 16px;
  }

  h3,
  h4 {
    font-family: var(--font-paragraph-default);
  }

  h4 {
    font-size: 14px;
    color: var(--color-text-labels);
  }

  h3 {
    font-size: 16px;
    font-family: var(--font-paragraph-default);
    color: var(--color-text-paragraph);
  }

  h3.bold {
    font-family: var(--font-paragraph-bold);
  }
`;

const ContentContainer = styled.form`
  h2:not(h2:first-child) {
    margin-top: 32px;
  }

  fieldset {
    padding: 16px;
    padding-top: 10px;
    padding-bottom: 30px;
    border: 1px solid var(--color-light-200);
    border-radius: 8px;
    box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1);
  }
`;

const RowContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;

  &:first-of-type {
    & > div > button:first-child {
      width: 316px;
    }
    & > *:nth-child(2) {
      margin-left: 16px;
      width: 316px;
    }
    & > *:nth-child(3) {
      width: 20px;
      height: 20px;
      color: var(--color-dark-blue-300);
      margin-left: 10px;
    }
  }

  & > p {
    font-family: var(--font-paragraph-default);
    font-size: 14px;
    color: var(--color-text-paragraph);
  }

  .selected {
    display: flex;
    align-items: center;
    margin-left: 12px;

    output {
      background: var(--color-light-100);
      border-radius: 4px;
      padding: 4px 8px;
    }
  }

  &.lastRow {
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const InfoContainer = styled.div<{ $isSidebarFixed?: boolean }>`
  display: flex;
  align-items: center;

  svg {
    color: var(--color-dark-blue-600);
    width: 17px;
    height: 17px;
    margin-right: 5px;
  }

  strong {
    font-family: var(--font-paragraph-bold);
  }

  ${({ $isSidebarFixed }) =>
    $isSidebarFixed &&
    `
    @media (max-width: 1440px){
      p {
        max-width: 600px;
      }
    }
  `}

  p,
  a {
    font-family: var(--font-paragraph-default);
  }

  strong,
  p {
    color: var(--color-text-paragraph);
  }

  a {
    color: var(--color-main-blue);
  }

  strong,
  default,
  a {
    font-size: 14px;
  }
`;

const ActionContainer = styled.div`
  display: flex;

  & > *:nth-child(2) {
    margin-left: 16px;
  }
`;

type TUsersFormProps = {
  variant: "create" | "edit";
};

const UsersForm: React.FC<TUsersFormProps> = ({ variant }) => {
  // Track deselected UUIDs globally using a ref
  const deselectedUuidsRef = useRef<string[]>([]);
  const dispatch = useTypedDispatch();

  const { uuid } = useParams<{ uuid: string }>();

  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const [shouldOpenModal, setShouldOpenModal] = useState(false);

  const [editableUser, setEditableUser] = useState<TMembership | null>(null);
  const [, /* fillAccessProfile */ setFillAccessProfile] = useState("");

  const reduxQuerystring = useTypedSelector((state) => state.querystring);

  const organizationData = useTypedSelector(
    (state) => state.settingsUserOrganizations.data
  );

  const isLoadingOrganizations = useTypedSelector(
    (state) => state.settingsUserOrganizations.loading
  );

  const { data: membershipsData } = useTypedSelector(
    (state) => state.memberships
  );

  const currentMembershipRef = useRef<TMembership | undefined>(undefined);

  const allOrganizationsRef = useRef<TOrganization[]>([]);
  const searchPhraseRef = useRef<string | undefined>();

  const shouldAllowConfirmModalSelection = useRef(false);

  const pageRef = useRef<number | undefined>();

  const [_shouldUpdateComponent, setShouldUpdateComponent] = useState(false);

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const [accessProfile, setAccessProfile] = useState("");
  const [accessProfileError, setAccessProfileError] = useState("");

  const isActiveUserRef = useRef(false);

  const [selectedOrganizations, setSelectedOrganizations] = useState<
    TOrganization[]
  >([]);

  const [tableSelectedOrganizations, setTableSelectedOrganizations] = useState<
    TOrganization[]
  >([]);

  const { goTo, setQuerystring } = useNavigationManager();

  const handleModalOnSelect = useCallback(
    (selected: TTableSelected[]) => {
      if (!shouldAllowConfirmModalSelection.current)
        shouldAllowConfirmModalSelection.current = true;

      if (selected[0].elementId === "all" && allOrganizationsRef.current) {
        setTableSelectedOrganizations(allOrganizationsRef.current);
        deselectedUuidsRef.current = [];
      } else if (selected[0].elementId === "none") {
        setTableSelectedOrganizations([]);
        deselectedUuidsRef.current = allOrganizationsRef.current.map(
          (org) => org.uuid
        );
      } else {
        // Merge previous selections with new selections from current page
        const prevSelected = tableSelectedOrganizations;
        const newSelected = allOrganizationsRef.current.filter((org) =>
          selected.some((select) => org.uuid === select.elementId)
        );

        // Merge by uuid, avoiding duplicates
        const merged = [
          ...prevSelected,
          ...newSelected.filter(
            (org) => !prevSelected.some((prev) => prev.uuid === org.uuid)
          ),
        ];

        // Track deselected items
        const currentPageUuids = (organizationData?.results ?? []).map(
          (org) => org.uuid
        );
        const selectedUuids = selected.map((s) => s.elementId);

        const newlyDeselected = currentPageUuids.filter(
          (uuid) => !selectedUuids.includes(uuid)
        );
        const nextDeselectedUuids = [
          ...deselectedUuidsRef.current.filter(
            (uuid) => !currentPageUuids.includes(uuid)
          ),
          ...newlyDeselected,
        ];
        deselectedUuidsRef.current = nextDeselectedUuids;
        const cleaned = merged.filter(
          (org) => !nextDeselectedUuids.includes(org.uuid)
        );

        setTableSelectedOrganizations(cleaned);
      }
    },
    [
      tableSelectedOrganizations,
      organizationData?.results,
      allOrganizationsRef.current,
    ]
  );

  const handleOpenModal = () => {
    setShouldOpenModal(true);
  };

  const handleModalOnCancel = () => {
    shouldAllowConfirmModalSelection.current = false;
    setShouldOpenModal(false);

    setTableSelectedOrganizations(selectedOrganizations);
    setShouldUpdateComponent(true);
    setTimeout(() => {
      setShouldUpdateComponent(false);
    }, 30);
  };

  const handleModalOnSuccess = () => {
    setSelectedOrganizations(tableSelectedOrganizations);
    setShouldOpenModal(false);
  };

  const handleCheckEmail = () => {
    if (email.length === 0) {
      return "Campo obrigatório";
    } else if (variant !== "edit" && !validateEmail(email)) {
      return "E-mail inválido";
    } else {
      return "";
    }
  };

  const handleAccessProfileOnSelect = (selected: string) => {
    setFillAccessProfile("");
    setAccessProfile(selected);
  };

  const onSubmitHandler = () => {
    const onSuccess = async (response: any) => {
      await dispatch(
        setOrganizationsByMembershipThunk({
          membershipUuid: response.uuid,
          organizationsUuid: selectedOrganizations.map(
            (selectedOrganization) => selectedOrganization.uuid
          ),
          cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
        })
      )
        .unwrap()
        .then(() => {
          dispatch(
            setToastState({
              variant: "success",
              shouldShow: true,
              message: {
                title: "Sucesso",
                description:
                  variant === "create"
                    ? "O novo usuário foi criado"
                    : "O cadastro foi atualizado",
              },
            })
          );
          goTo(ROUTES.SETTINGS_USERS, getQuerystringParams());
        })
        .catch((e) => {
          const errorMessage = backendErrorMessageHandler(e);
          dispatch(
            setToastState({
              variant: "error",
              shouldShow: true,
              message: {
                title: "Erro",
                description:
                  typeof errorMessage === "string"
                    ? errorMessage
                    : variant === "create"
                    ? "Não foi possível criar o usuário, revise os campos e tente novamente"
                    : "Não foi possível atualizar o cadastro, revise os campos e tente novamente",
              },
            })
          );
        });
    };

    const emailError = handleCheckEmail();

    const accessProfileError = accessProfile.length === 0;

    if (emailError.length > 0 && accessProfileError) {
      setEmailError(emailError);
      setAccessProfileError("Campo obrigatório");
    } else if (emailError.length > 0) {
      setEmailError(emailError);
      setAccessProfileError("");
    } else if (accessProfileError) {
      setAccessProfileError("Campo obrigatório");
      setEmailError("");
    } else {
      setEmailError("");
      setAccessProfileError("");
      let role = decideMembershipRole(
        accessProfile as "Administrador" | "Apenas leitura" | "Colaborador"
      ) as TMembershipRole;

      const newMemberData = {
        account: (organizationData as TOrganizationData)?.results[0]?.account
          ?.uuid,
        email: email,
        role: role,
        is_active: isActiveUserRef.current,
        organizations: selectedOrganizations.map(
          (organization) => organization.uuid
        ),
      };

      if (variant === "create") {
        dispatch(
          createMemberThunk({
            member: newMemberData,
            cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
          })
        )
          .unwrap()
          .then((response) => {
            if (
              response &&
              typeof response.uuid === "string" &&
              response.uuid.length > 0
            ) {
              onSuccess(response);
            } /* else {
              dispatch(
                setToastState({
                  variant: "error",
                  shouldShow: true,
                  message: {
                    title: "Erro",
                    description:
                      "Não foi possível obter o identificador do usuário recém-criado. Tente novamente.",
                  },
                })
              );
            } */
          })
          .catch((e: AxiosError) => {
            const errorMessage = backendErrorMessageHandler(e);

            const error = e?.response?.data as { email?: string[] };
            const emailInUse = "Already exists";
            if (error?.email && error.email.includes(emailInUse)) {
              setEmailError("e-mail em uso");
            }
            dispatch(
              setToastState({
                variant: "error",
                shouldShow: true,
                message: {
                  title: "Erro",
                  description:
                    typeof errorMessage === "string"
                      ? errorMessage
                      : "Não foi possível criar o usuário, revise os campos e tente novamente",
                },
              })
            );
          });
      } else {
        if (editableUser) {
          dispatch(
            editMemberThunk({
              uuid: editableUser.uuid,
              member: newMemberData,
              cancelToken: (cancelTokenSource.current as CancelTokenSource)
                .token,
            })
          )
            .unwrap()
            .then((response) => {
              setTimeout(() => {
                onSuccess(response);
              }, 1000);
            })
            .catch((e) => {
              const errorMessage = backendErrorMessageHandler(e);
              dispatch(
                setToastState({
                  variant: "error",
                  shouldShow: true,
                  message: {
                    title: "Erro",
                    description:
                      typeof errorMessage === "string"
                        ? errorMessage
                        : "Não foi possível atualizar o cadastro, revise os campos e tente novamente",
                  },
                })
              );
            });
        }
      }
    }
  };

  const handlePopulateData = useCallback(async () => {
    if (membershipsData.count > 0 && cancelTokenSource.current) {
      currentMembershipRef.current = membershipsData.results.find(
        (membership) => membership.uuid === uuid
      );
      if (currentMembershipRef.current) {
        setEmail(
          currentMembershipRef.current.email ??
            currentMembershipRef.current.user.email ??
            ""
        );

        setFillAccessProfile(
          decideMembershipRole(currentMembershipRef.current.role) as string
        );
        setAccessProfile(
          decideMembershipRole(currentMembershipRef.current.role) as string
        );
        setEditableUser(currentMembershipRef.current);
        isActiveUserRef.current = currentMembershipRef.current.is_active;

        // Only merge organizations if tableSelectedOrganizations is empty
        if (tableSelectedOrganizations.length === 0) {
          apiService
            .getOrganizationsByMembership(
              currentMembershipRef.current.uuid,
              (cancelTokenSource.current as CancelTokenSource).token
            )
            .then((response) => {
              if (Array.isArray(response?.data)) {
                // Merge fetched organizations with current selections
                const fetched = response.data;
                const merged = [
                  ...tableSelectedOrganizations,
                  ...fetched.filter(
                    (org) =>
                      !tableSelectedOrganizations.some(
                        (sel) => sel.uuid === org.uuid
                      )
                  ),
                ];

                setTableSelectedOrganizations(merged);
                setSelectedOrganizations(merged);
              } else {
                apiService
                  .getOrganizationsByMembership(
                    (currentMembershipRef.current as TMembership).uuid,
                    (cancelTokenSource.current as CancelTokenSource).token
                  )
                  .then((response) => {
                    if (Array.isArray(response?.data)) {
                      // Merge fetched organizations with current selections
                      const fetched = response.data;
                      const merged = [
                        ...tableSelectedOrganizations,
                        ...fetched.filter(
                          (org) =>
                            !tableSelectedOrganizations.some(
                              (sel) => sel.uuid === org.uuid
                            )
                        ),
                      ];
                      setTableSelectedOrganizations(merged);
                      setSelectedOrganizations(merged);
                    }
                  });
              }
            });
        }
      }
    }
  }, [uuid, membershipsData]);

  const getOrganizations = async () => {
    cancelTokenSource.current = axios.CancelToken.source();

    let querystring = { ...reduxQuerystring };

    dispatch(
      fetchOrganizationsForUserSettings({
        cancelToken: cancelTokenSource.current.token,
      })
    );

    await apiService
      .getAllOrganizations(
        querystring.account_id,
        cancelTokenSource.current.token
      )
      .then((response: TOrganization[]) => {
        allOrganizationsRef.current = response;
      });
  };

  useEffect(() => {
    if (membershipsData.count > 0 && variant === "edit") {
      setTimeout(() => {
        handlePopulateData();
      }, 200);
    }
  }, [variant, handlePopulateData]);

  useEffect(() => {
    if (reduxQuerystring.account_id && reduxQuerystring.account_id.length > 0) {
      getOrganizations();
    }

    return () => {
      if (cancelTokenSource.current) cancelTokenSource.current.cancel();
    };
  }, [reduxQuerystring.account_id]);

  // Apply global deselection filter whenever organizations or deselectedUuids change
  useEffect(() => {
    setTableSelectedOrganizations((prevSelected) =>
      prevSelected.filter(
        (org) => !deselectedUuidsRef.current.includes(org.uuid)
      )
    );
  }, [organizationData?.results]);

  useEffect(() => {
    setTimeout(() => {
      const querystring = getQuerystringParams();
      if (querystring.users_search) {
        delete querystring.users_search;
        setQuerystring(querystring);
      }
    });

    return () => {
      const querystring = getQuerystringParams();
      if (querystring.users_search) {
        delete querystring.users_search;
        setQuerystring(querystring);
      }
    };
  }, []);

  return (
    <Container>
      <BreadCrumbs
        names={[
          "Início",
          "Configurações",
          "Usuários",
          variant === "create" ? "Criar" : "Editar",
        ]}
        urls={[
          ROUTES.HOME,
          ROUTES.SETTINGS,
          ROUTES.SETTINGS_USERS,
          variant === "create"
            ? ROUTES.SETTINGS_USERS_CREATE
            : ROUTES.SETTINGS_USERS_EDIT,
        ]}
      />
      <h1>{variant === "create" ? "Criar usuário" : "Editar usuário"}</h1>
      <ContentContainer
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <fieldset>
          <h2>Dados do usuário</h2>
          <RowContainer>
            <LabeledTextInput
              placeholder="E-mail *"
              onError={{ hasError: emailError.length > 0, message: emailError }}
              onChange={(inputValue) => {
                setTimeout(() => {
                  setEmail(inputValue);
                }, 100);
              }}
              filled={
                editableUser
                  ? editableUser.email ?? editableUser.user.email
                  : undefined
              }
              disabled={variant === "edit"}
            />

            <Dropdown
              button={{
                variant: "labeled",
                labels: ["Perfil de acesso *"],
                placeholder: "Perfil de acesso *",
                showPlaceholder: true,
                filled: accessProfile.length > 0 ? accessProfile : undefined,
                onError: {
                  hasError: accessProfileError.length > 0,
                  message: accessProfileError,
                },
              }}
              menu={{
                variant: "unlabeled",
                options: ["Administrador", "Colaborador", "Apenas leitura"],
                search: false,
                onSelect: handleAccessProfileOnSelect,
                forceSelectOptionsByIndex: [
                  ["Administrador", "Colaborador", "Apenas leitura"].indexOf(
                    accessProfile
                  ),
                ],
              }}
            />
            <Tooltip
              text={[
                {
                  style: "default",
                  text: "Tipos de perfis de acesso:",
                  shouldBreakLine: true,
                },
                {
                  style: "default",
                  text: "Administrador: Pode criar, editar e excluir usuários e Organizações.",
                  shouldBreakLine: true,
                },
                {
                  style: "default",
                  text: "",
                  shouldBreakLine: true,
                },
                {
                  style: "default",
                  text: "Colaborador: Pode criar outros usuários do tipo “colaborador” ou “Apenas leitura”, mas não pode editar ou excluir perfis de usuários e pode apenas visualizar as Organizações.",
                  shouldBreakLine: true,
                },
                {
                  style: "default",
                  text: "",
                  shouldBreakLine: true,
                },
                {
                  style: "default",
                  text: "Apenas leitura: Pode apenas visualizar as informações, porém não pode realizar ações.",
                  shouldBreakLine: true,
                },
              ]}
              size="large"
            >
              <UilInfoCircle />
            </Tooltip>
          </RowContainer>
          <RowContainer>
            <ToggleSwitch
              onClick={() => {
                isActiveUserRef.current = !isActiveUserRef.current;
              }}
              id="activeUser"
              key={
                `${
                  editableUser !== null &&
                  editableUser !== undefined &&
                  editableUser.is_active
                }` + "-activeUser"
              }
              initialState={
                editableUser !== null &&
                editableUser !== undefined &&
                editableUser.is_active
              }
            />
            <label htmlFor="activeUser">
              <p>Usuário ativo</p>
            </label>
          </RowContainer>
          <h2>Organizações</h2>
          <h4>Quais organizações o usuário terá acesso</h4>
          <RowContainer>
            <Button
              variant="outline"
              icon="noIcon"
              data-testid="select-organizations-button"
              text="Selecionar organizações"
              onClick={handleOpenModal}
            />
            <div className="selected">
              <output>
                <h3 className="bold">{selectedOrganizations?.length}</h3>
              </output>
              <h3>
                &nbsp;
                {selectedOrganizations?.length === 1
                  ? "selecionada"
                  : "selecionadas"}
              </h3>
            </div>
          </RowContainer>
        </fieldset>
      </ContentContainer>
      <RowContainer className="lastRow">
        <InfoContainer $isSidebarFixed={reduxQuerystring.sidebar_fixed}>
          <UilInfoCircle />
          <p>
            <strong>Importante: </strong>O usuário precisa ter uma conta
            no&nbsp;
            <a
              href="https://id.nexaas.com/start_sign_up"
              target="_blank"
              rel="noopener noreferrer"
            >
              Nexaas ID
            </a>
            &nbsp;com o mesmo e-mail para acessar o Portal Fiscal
          </p>
        </InfoContainer>
        <ActionContainer>
          <Button
            variant="secondary"
            icon="noIcon"
            text="Cancelar"
            onClick={() => {
              goTo(ROUTES.SETTINGS_USERS);
            }}
          />
          <Button
            variant="primary"
            icon="checkmark"
            iconPosition="left"
            text={variant === "create" ? "Criar usuário" : "Editar usuário"}
            onClick={onSubmitHandler}
          />
        </ActionContainer>
      </RowContainer>
      <Modal
        title="Selecionar Organizações"
        shouldOpen={shouldOpenModal}
        onClose={handleModalOnCancel}
        main={{
          style: "table",
          content: {
            disableBatchOperations: true,
            columns: {
              tableHead: {
                width: "0px 50px 300px 290px".split(" "),
              },
              tableBody: {
                width: "0px 50px 300px 290px".split(" "),
              },
            },
            search: {
              placeholder: "Buscar por Nome ou CNPJ",
              id: "select-organizations-search",
              onSearch(searchPrase) {
                searchPhraseRef.current = searchPrase;

                const querystring = { ...reduxQuerystring };
                querystring.users_search = searchPrase;
                setQuerystring(querystring);

                getOrganizations();
              },
              onClear() {
                searchPhraseRef.current = "";

                const querystring = { ...reduxQuerystring };
                delete querystring.users_search;
                setQuerystring(querystring);

                getOrganizations();
              },
            },
            row: "singleRow",
            selectType: {
              type: "multiSelect",
              fullData: allOrganizationsRef.current,
              initialSelectedState: tableSelectedOrganizations,
            },
            tableHead: {
              customTopPosition: "-24px",
              value: [
                {
                  style: "noIcon",
                  showSkeleton: isLoadingOrganizations,
                  value: "Nome",
                },
                {
                  style: "noIcon",
                  showSkeleton: isLoadingOrganizations,
                  value: "CNPJ",
                },
              ],
            },
            isLoading: isLoadingOrganizations,
            tableBody: isLoadingOrganizations
              ? new Array(5).fill("").map(() => ({
                  elementId: "",
                  file_nfe: null,
                  file_xml: null,
                  body: [
                    {
                      style: "default",
                      value: "",
                      skeleton: { values: [{ width: "266px" }] },
                    },
                    {
                      style: "default",
                      value: "",
                      skeleton: { values: [{ width: "127px" }] },
                    },
                  ] as TTableBody<TCellStyleVariants>[],
                }))
              : (organizationData?.results ?? []).map(
                  (organization: TOrganization) => ({
                    elementId: organization.uuid,
                    file_nfe: null,
                    file_xml: null,
                    body: [
                      { style: "default", value: organization.company_name },
                      { style: "default", value: cnpjMask(organization.cnpj) },
                    ],
                  })
                ),
            onSelect: handleModalOnSelect,
            footer: {
              customPagination: organizationData
                ? {
                    currentPage: `${organizationData?.current_page}`,
                    buttonBackward: {
                      disabled: organizationData.current_page <= 1,
                      onClick() {
                        const prevPage = organizationData.current_page - 1;
                        if (prevPage >= 1) {
                          pageRef.current = prevPage;
                          const querystring = { ...reduxQuerystring };
                          querystring.page = pageRef.current;
                          if (searchPhraseRef.current)
                            querystring.users_search = searchPhraseRef.current;
                          setQuerystring(querystring);
                          dispatch(
                            fetchOrganizationsForUserSettings({
                              cancelToken: (
                                cancelTokenSource.current as CancelTokenSource
                              ).token,
                            })
                          );
                        }
                      },
                    },
                    buttonForward: {
                      disabled:
                        organizationData.current_page >=
                        organizationData.pages.length,
                      onClick() {
                        const nextPage = organizationData.current_page + 1;

                        if (nextPage <= organizationData.pages.length) {
                          pageRef.current = nextPage;
                          const querystring = { ...reduxQuerystring };
                          querystring.page = pageRef.current;
                          if (searchPhraseRef.current)
                            querystring.users_search = searchPhraseRef.current;
                          setQuerystring(querystring);
                          dispatch(
                            fetchOrganizationsForUserSettings({
                              cancelToken: (
                                cancelTokenSource.current as CancelTokenSource
                              ).token,
                            })
                          );
                        }
                      },
                    },
                    pages: organizationData.pages.map((page) => {
                      // Always use the actual page number for querystring.page
                      const pageKey = Object.keys(page)[0];
                      const pageUrl = Object.values(page)[0] as string;
                      let pageNumber = 1;
                      // If pageKey is a number, use it directly
                      if (!isNaN(Number(pageKey))) {
                        pageNumber = Number(pageKey);
                      } else {
                        // Otherwise, try to parse from URL
                        const match = pageUrl.match(/.*?page=(\d+)/);
                        if (match && match[1]) {
                          pageNumber = parseInt(match[1]);
                        }
                      }
                      return {
                        value: pageKey,
                        onClick() {
                          pageRef.current = pageNumber;
                          const querystring = { ...reduxQuerystring };
                          querystring.page = pageRef.current;
                          if (searchPhraseRef.current)
                            querystring.users_search = searchPhraseRef.current;
                          setQuerystring(querystring);
                          dispatch(
                            fetchOrganizationsForUserSettings({
                              cancelToken: (
                                cancelTokenSource.current as CancelTokenSource
                              ).token,
                            })
                          );
                        },
                      };
                    }),
                  }
                : undefined,
            },
          },
        }}
        footer={{
          style: "default",
          "data-testid": "selected-organizations-count",
          text: [
            {
              style: "bold",
              shouldBreakLine: false,
              text: tableSelectedOrganizations?.length + " ",
            },
            {
              style: "default",
              shouldBreakLine: false,
              text:
                tableSelectedOrganizations?.length !== 1
                  ? "Organizações selecionadas"
                  : "Organização selecionada",
            },
          ],
          buttons: [
            {
              variant: "secondary",
              icon: "noIcon",
              text: "Cancelar",
              onClick() {
                handleModalOnCancel();
              },
            },
            {
              variant: "primary",
              iconPosition: "left",
              icon: "checkmark",
              text: "Confirmar seleção",
              onClick() {
                handleModalOnSuccess();
              },
              disabled: !shouldAllowConfirmModalSelection.current,
            },
          ],
        }}
      />
    </Container>
  );
};

export default UsersForm;
